import React, { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import BaseTableAction from "../../Base/BaseTableAction";
import BaseSelect from "../../Base/BaseSelect";
import MultiSelectSearchable from "../../Base/MultiSelectSearchable";
import { Service, ServiceRouteStop } from "../../../models/ServiceModel";
import { ServiceStatus } from "../../../enums/ServiceEnum";
import "./styles/ServicesTable.css";
import { formatDateDDMMYYYY } from "../../../utils/formatDate";
import ServiceDateTimeRangeFilter from "../filters/ServiceDateTimeRangeFilter";
import { useSessionStorageState } from "../../../hooks/useSessionStorageState";
import {
  applyGeneralModeDateDefaults,
  getGeneralModeDefaultStartDateYmd,
} from "../../../utils/serviceGeneralModeDefaults";
import { isEnaexCompany } from "../../../config/adicionalCompany";

export type ServicesTableFilters = {
  id: string;
  generalMode: boolean;
  /** Últimos servicios agregados (orden por fecha de creación, sin ventana diaria) */
  recentMode: boolean;
  fechaDesde: string;
  fechaHasta: string;
  horaDesde: string;
  horaHasta: string;
  origen: string;
  destino: string;
  empresa: string;
  centroCosto: string;
  enaexCode: string;
  estado: string;
  /** Salidas próximas: "1" | "2" | "3" horas o menos (vacío = sin filtro) */
  proximasHoras: string;
  pasajeros: number[];
};

/** Clave de sessionStorage donde se persisten los filtros del listado de servicios */
export const SERVICES_TABLE_FILTERS_STORAGE_KEY = "services_table_filters";

/** Clave de sessionStorage donde se persiste el orden elegido por el usuario */
export const SERVICES_TABLE_SORT_STORAGE_KEY = "services_table_sort";

export const createDefaultServicesTableFilters = (): ServicesTableFilters => ({
  id: "",
  generalMode: false,
  recentMode: false,
  fechaDesde: "",
  fechaHasta: "",
  horaDesde: "",
  horaHasta: "",
  origen: "",
  destino: "",
  empresa: "",
  centroCosto: "",
  enaexCode: "",
  estado: "",
  proximasHoras: "",
  pasajeros: [],
});

type Props = {
  data: Service[];
  /** Total en servidor (paginación); si no se pasa, usa data.length */
  totalCount?: number;
  companyOptions?: Array<{ value: string; label: string }>;
  passengerOptions?: Array<{ value: string; label: string }>;
  loading?: boolean;
  onEdit: (item: Service) => void;
  onDelete: (id: number) => void;
  /** Abrir modal para modificar origen/destino de la ruta. */
  onEditRoute?: (item: Service) => void;
  onDuplicate?: (item: Service) => void;
  onRetorno?: (item: Service) => void;
  onStatusTransition?: (item: Service, nextStatus: ServiceStatus) => void | Promise<void>;
  statusTransitionLoadingIds?: number[];
  /** Asignar conductor desde planilla (solo AUTORIZADO). */
  onAssignDriver?: (item: Service, driverId: number) => void | Promise<void>;
  assignDriverLoadingIds?: number[];
  /** Desasignar conductor (solo ASIGNADO; visible deshabilitado en EN_CURSO). */
  onReleaseDriver?: (item: Service) => void;
  releaseDriverLoadingIds?: number[];
  driverOptionsByCompany?: Record<number, Array<{ value: number; label: string; is_available?: boolean }>>;
  onLoadDriversForCompany?: (companyId: number) => void | Promise<void>;
  driversLoadingCompanyIds?: number[];
  onFiltersChange?: (filters: ServicesTableFilters) => void;
};

function formatCargaDiariaInstant(d: Date): string {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm} ${hh}:${min}`;
}

function getCargaDiariaRangeFromNow(now: Date): { desde: string; hasta: string } {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  end.setHours(23, 59, 0, 0);
  return {
    desde: formatCargaDiariaInstant(start),
    hasta: formatCargaDiariaInstant(end),
  };
}

/** Clave YYYY-MM-DD para diferenciar chips de fecha con la misma paleta. */
function toServiceDateKey(serviceDate: unknown): string {
  const raw = String(serviceDate ?? "").trim();
  if (!raw) return "";
  const dayPart = raw.split("T")[0].trim();
  const parts = dayPart.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[0]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
  }
  return dayPart;
}

const CARGA_DIARIA_REFRESH_MS = 60_000;

/** Texto legible + ícono; el color refuerza la categoría pero no es la única señal. */
const STATUS_DISPLAY: Record<string, { label: string; icon: React.ReactNode }> = {
  PREPARACION: {
    label: "Preparación",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  PROGRAMADO: {
    label: "Programado",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  AUTORIZADO: {
    label: "Autorizado",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  ASIGNADO: {
    label: "Asignado",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  EN_CURSO: {
    label: "En curso",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  COMPLETADO: {
    label: "Completado",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  CANCELADO: {
    label: "Cancelado",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  ANULADO: {
    label: "Anulado",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M4.93 4.93l14.14 14.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
};

function getStatusDisplay(status: string): { label: string; icon: React.ReactNode } {
  const key = String(status || "").trim();
  if (STATUS_DISPLAY[key]) return STATUS_DISPLAY[key];
  return {
    label: key ? key.replace(/_/g, " ") : "Sin estado",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  };
}

function getNextStatus(status: string): ServiceStatus | null {
  if (status === ServiceStatus.PREPARACION) return ServiceStatus.AUTORIZADO;
  if (status === ServiceStatus.ASIGNADO) return ServiceStatus.EN_CURSO;
  if (status === ServiceStatus.EN_CURSO) return ServiceStatus.COMPLETADO;
  return null;
}

function splitPassengerLabel(raw: unknown): { name: string; extra?: string } {
  const s = String(raw ?? "").trim();
  if (!s) return { name: "—" };
  const m = s.match(/^(.*?)(?:\s*\+(\d+))$/);
  if (!m) return { name: s };
  const name = (m[1] ?? "").trim() || s;
  const extra = m[2] ? `+${m[2]}` : undefined;
  return { name, extra };
}

function getPassengerFullName(raw: unknown): string {
  return String(raw ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getInitial(name: string): string {
  const clean = String(name || "").trim();
  return clean && clean !== "—" ? clean.charAt(0).toUpperCase() : "?";
}

function formatTimeHHMM(raw: unknown): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return s;
  const hh = String(m[1]).padStart(2, "0");
  const mm = String(m[2]).padStart(2, "0");
  return `${hh}:${mm}`;
}

function getCompanyColorStyle(companyName: unknown): React.CSSProperties {
  const palettes = [
    { bg: "#eff6ff", border: "#bfdbfe", text: "#1e40af" },
    { bg: "#ecfeff", border: "#a5f3fc", text: "#155e75" },
    { bg: "#f0fdf4", border: "#bbf7d0", text: "#166534" },
    { bg: "#fffbeb", border: "#fde68a", text: "#92400e" },
    { bg: "#fdf4ff", border: "#f5d0fe", text: "#86198f" },
    { bg: "#fff1f2", border: "#fecdd3", text: "#9f1239" },
    { bg: "#eef2ff", border: "#c7d2fe", text: "#3730a3" },
    { bg: "#f8fafc", border: "#e2e8f0", text: "#0f172a" },
  ];

  const value = String(companyName ?? "").trim();
  if (!value) {
    const fallback = palettes[7];
    return {
      ["--company-bg" as any]: fallback.bg,
      ["--company-border" as any]: fallback.border,
      ["--company-text" as any]: fallback.text,
    };
  }

  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  const selected = palettes[hash % palettes.length];

  return {
    ["--company-bg" as any]: selected.bg,
    ["--company-border" as any]: selected.border,
    ["--company-text" as any]: selected.text,
  };
}

/* ====================================
   ORDENAMIENTO POR COLUMNAS
   ==================================== */

export type ServicesSortKey =
  | "id"
  | "empresa"
  | "fecha"
  | "hora"
  | "usuarios"
  | "origen"
  | "destino"
  | "autorizador"
  | "conductor"
  | "estado";

export type ServicesSortState = { key: ServicesSortKey; dir: "asc" | "desc" } | null;

/** Milisegundos de fecha+hora del servicio; NaN-safe (los inválidos van al final). */
function toScheduleMs(s: any): number | null {
  const date = String(s?.service_date ?? "").trim();
  const time = String(s?.service_time ?? "").trim();
  if (!date) return null;
  const dt = new Date(`${date}T${time || "00:00"}`);
  const ms = dt.getTime();
  return Number.isNaN(ms) ? null : ms;
}

type MissingDriverUrgency = "soon" | "near" | "critical";

function isVoidedStatus(status: string): boolean {
  return status === ServiceStatus.CANCELADO || status === ServiceStatus.ANULADO;
}

/** Sin conductor y próximo a iniciar (o ya vencido sin asignar). */
function getMissingDriverUrgency(
  s: { status?: string; driver?: number | null; driver_name?: string | null; service_date?: string; service_time?: string },
  nowMs: number
): MissingDriverUrgency | null {
  const status = String(s.status || "");
  if (isVoidedStatus(status)) return null;
  if (
    status === ServiceStatus.ASIGNADO ||
    status === ServiceStatus.EN_CURSO ||
    status === ServiceStatus.COMPLETADO
  ) {
    return null;
  }
  const hasDriver =
    Boolean(s.driver) || Boolean(String(s.driver_name ?? "").trim());
  if (hasDriver) return null;

  const startMs = toScheduleMs(s);
  if (startMs == null) return null;
  const hours = (startMs - nowMs) / (60 * 60 * 1000);
  if (hours <= 1) return "critical";
  if (hours <= 2) return "near";
  if (hours <= 3) return "soon";
  return null;
}

function getPrimaryPassengerName(s: any): string {
  const details = Array.isArray(s?.passenger_details) ? s.passenger_details : [];
  const fromDetails = details
    .map((p: any) => getPassengerFullName(p?.name))
    .find((n: string) => n && n !== "—");
  if (fromDetails) return fromDetails;
  const fallback = splitPassengerLabel(s?.passenger);
  return getPassengerFullName(fallback.name);
}

function getSortValue(s: any, key: ServicesSortKey): number | string | null {
  switch (key) {
    case "id":
      return s?.id != null ? Number(s.id) : null;
    case "empresa":
      return String(s?.company_name ?? "").trim() || null;
    case "fecha":
      return toScheduleMs(s);
    case "hora": {
      const time = formatTimeHHMM(s?.service_time);
      return time || null;
    }
    case "usuarios": {
      const name = getPrimaryPassengerName(s);
      return name && name !== "—" ? name : null;
    }
    case "origen":
      return String(s?.origin_name ?? "").trim() || null;
    case "destino":
      return String(s?.destination_name ?? "").trim() || null;
    case "autorizador":
      return String(s?.authorizer_name ?? "").trim() || null;
    case "conductor":
      return String(s?.driver_name ?? "").trim() || null;
    case "estado":
      return getStatusDisplay(String(s?.status ?? "")).label || null;
    default:
      return null;
  }
}

function sortServices(data: Service[], sort: NonNullable<ServicesSortState>): Service[] {
  const dirMul = sort.dir === "asc" ? 1 : -1;
  const sorted = [...data];

  sorted.sort((a, b) => {
    const va = getSortValue(a, sort.key);
    const vb = getSortValue(b, sort.key);

    // Valores faltantes siempre al final, sin importar la dirección
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;

    let cmp = 0;
    if (typeof va === "number" && typeof vb === "number") {
      cmp = va - vb;
    } else {
      cmp = String(va).localeCompare(String(vb), "es", { sensitivity: "base", numeric: true });
    }
    if (cmp !== 0) return cmp * dirMul;

    // Desempate estable: fecha+hora y luego ID
    const ma = toScheduleMs(a) ?? Number.MAX_SAFE_INTEGER;
    const mb = toScheduleMs(b) ?? Number.MAX_SAFE_INTEGER;
    if (ma !== mb) return ma - mb;
    return Number((a as any)?.id ?? 0) - Number((b as any)?.id ?? 0);
  });

  return sorted;
}

/* Columnas de la tabla: label + ícono + clave de orden (null = no ordenable) */
const TABLE_COLUMNS: Array<{
  sortKey: ServicesSortKey | null;
  label: string;
  icon?: React.ReactNode;
}> = [
  {
    sortKey: null,
    label: "ID",
  },
  {
    sortKey: null,
    label: "Empresa",
  },
  {
    sortKey: null,
    label: "Conductor",
  },
  {
    sortKey: "fecha",
    label: "Fecha",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    sortKey: "hora",
    label: "Hora",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    sortKey: "usuarios",
    label: "Pasajeros",
  },
  {
    sortKey: "origen",
    label: "Origen",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
        <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    sortKey: "destino",
    label: "Destino",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    sortKey: "autorizador",
    label: "Autorizador",
  },
  {
    sortKey: "estado",
    label: "Estado",
  },
];

/** Orden canónico de estados para los chips de filtro */
const CANONICAL_STATUS_ORDER = [
  "PREPARACION",
  "PROGRAMADO",
  "AUTORIZADO",
  "ASIGNADO",
  "EN_CURSO",
  "COMPLETADO",
  "CANCELADO",
  "ANULADO",
];

/** Chips de estado visibles siempre, aunque no haya datos con ese estado */
const DEFAULT_STATUS_CHIPS = [
  "PREPARACION",
  "AUTORIZADO",
  "ASIGNADO",
  "EN_CURSO",
  "COMPLETADO",
];

const PROXIMAS_HORAS_OPTIONS = [
  { value: "1", label: "En 1 hora o menos" },
  { value: "2", label: "En 2 horas o menos" },
  { value: "3", label: "En 3 horas o menos" },
];

type ActiveFilterChipKey =
  | "viewMode"
  | "id"
  | "empresa"
  | "enaexCode"
  | "fechaDesde"
  | "origen"
  | "destino"
  | "centroCosto"
  | "estado"
  | "proximasHoras"
  | "pasajeros";

type ActiveFilterChip = {
  key: ActiveFilterChipKey;
  label: string;
  value: string;
};

const STATUS_COLOR_CLASSES: Record<string, string> = {
  PREPARACION: "status-preparacion",
  PROGRAMADO: "status-programado",
  AUTORIZADO: "status-autorizado",
  ASIGNADO: "status-asignado",
  EN_CURSO: "status-en-curso",
  COMPLETADO: "status-completado",
  CANCELADO: "status-cancelado",
  ANULADO: "status-anulado",
};

function getStatusColor(status: string): string {
  return STATUS_COLOR_CLASSES[status] || "status-default";
}

/* ====================================
   FILA MEMOIZADA
   Evita re-renderizar todas las filas cuando cambia estado local
   (menú de estado, reloj de carga diaria, filtros, etc.)
   ==================================== */

type ServiceRowProps = {
  s: Service;
  onEdit: (item: Service) => void;
  onDelete: (id: number) => void;
  onEditRoute?: (item: Service) => void;
  onDuplicate?: (item: Service) => void;
  onRetorno?: (item: Service) => void;
  onStatusTransition?: (item: Service, nextStatus: ServiceStatus) => void | Promise<void>;
  isStatusLoading: boolean;
  isStatusMenuOpen: boolean;
  onToggleStatusMenu: (id: number) => void;
  onAssignDriver?: (item: Service, driverId: number) => void | Promise<void>;
  isAssignDriverLoading: boolean;
  onReleaseDriver?: (item: Service) => void;
  isReleaseDriverLoading: boolean;
  driverOptions: Array<{ value: number; label: string; is_available?: boolean }>;
  isDriversLoading: boolean;
  onLoadDrivers?: () => void;
  /** Índice de tono por día (misma paleta cálida del chip de fecha). */
  dayToneIndex?: number | null;
  /** Reloj local para urgencia de salidas próximas sin conductor. */
  nowMs?: number;
};

function shortDriverOptionLabel(label: string): string {
  const head = String(label || "").split("|")[0]?.trim();
  return head || String(label || "").trim() || "Conductor";
}

type PassengerTooltipItem = { name: string; address: string };

function useUserTooltipPosition() {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>({});

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 10;
    const tooltipWidth = Math.min(360, window.innerWidth - margin * 2);
    let left = Math.min(r.left, window.innerWidth - tooltipWidth - margin);
    left = Math.max(margin, left);

    const spaceBelow = window.innerHeight - r.bottom - margin;
    const preferBelow = spaceBelow >= 140;

    setStyle({
      position: "fixed",
      left,
      width: tooltipWidth,
      zIndex: 15000,
      ...(preferBelow
        ? { top: r.bottom + 8, bottom: "auto" }
        : { top: "auto", bottom: window.innerHeight - r.top + 8 }),
    });
  }, []);

  const showTooltip = () => {
    updatePosition();
    setOpen(true);
  };

  const hideTooltip = () => setOpen(false);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const onReflow = () => updatePosition();
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open, updatePosition]);

  return { anchorRef, open, style, showTooltip, hideTooltip };
}

function UserPassengerTooltipCell({
  primaryName,
  extraCount,
  details,
}: {
  primaryName: string;
  extraCount: number;
  details: PassengerTooltipItem[];
}) {
  const { anchorRef, open, style, showTooltip, hideTooltip } = useUserTooltipPosition();

  const items = useMemo<PassengerTooltipItem[]>(
    () =>
      details.length > 0
        ? details
        : [{ name: primaryName || "—", address: "Sin dirección" }],
    [details, primaryName]
  );

  const tooltip =
    open &&
    createPortal(
      <div
        className="user-tooltip-pro user-tooltip-pro--portal"
        id="user-passenger-tooltip"
        role="tooltip"
        style={style}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        <p className="user-tooltip-title-pro">Pasajeros y direcciones</p>
        <ul className="user-tooltip-list-pro">
          {items.map((item, idx) => (
            <li key={`${item.name}-${idx}`} className="user-tooltip-item-pro">
              <span className="user-tooltip-passenger-pro">{item.name}</span>
              <span className="user-tooltip-address-pro">{item.address}</span>
            </li>
          ))}
        </ul>
      </div>,
      document.body
    );

  return (
    <>
      <div
        ref={anchorRef}
        className="user-cell-pro user-cell-with-tooltip-pro user-cell-pro--name-only"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        tabIndex={0}
        aria-describedby={open ? "user-passenger-tooltip" : undefined}
      >
        <div className="user-text-pro">
          <span className="user-name-pro">{primaryName || "—"}</span>
          {extraCount > 0 ? (
            <span
              className="user-extra-pro"
              aria-label={`Acompañantes: +${extraCount}`}
            >
              +{extraCount}
            </span>
          ) : null}
        </div>
      </div>
      {tooltip}
    </>
  );
}

/** Origen + badge "+N puntos" con tooltip del recorrido (origen → puntos → destino). */
function OriginStopsCell({
  originName,
  destinationName,
  stops,
}: {
  originName?: string | null;
  destinationName?: string | null;
  stops: ServiceRouteStop[];
}) {
  const tooltipId = React.useId();
  const { anchorRef, open, style, showTooltip, hideTooltip } = useUserTooltipPosition();
  const origin = String(originName ?? "").trim();

  if (stops.length === 0) {
    return (
      <div
        className="location-pill-pro location-origin"
        title={origin || undefined}
        aria-label={origin ? `Origen: ${origin}` : "Origen no indicado"}
      >
        {origin || "—"}
      </div>
    );
  }

  const countLabel = `+${stops.length} ${stops.length === 1 ? "punto" : "puntos"}`;
  const tooltip =
    open &&
    createPortal(
      <div
        className="user-tooltip-pro user-tooltip-pro--portal"
        id={tooltipId}
        role="tooltip"
        style={style}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        <p className="user-tooltip-title-pro">Recorrido con puntos adicionales</p>
        <ul className="user-tooltip-list-pro">
          <li className="user-tooltip-item-pro">
            <span className="user-tooltip-passenger-pro">{origin || "—"}</span>
            <span className="user-tooltip-address-pro">Origen</span>
          </li>
          {stops.map((stop, idx) => (
            <li key={stop.id} className="user-tooltip-item-pro">
              <span className="user-tooltip-passenger-pro">{stop.location_name || "—"}</span>
              <span className="user-tooltip-address-pro">Punto {idx + 1}</span>
            </li>
          ))}
          <li className="user-tooltip-item-pro">
            <span className="user-tooltip-passenger-pro">
              {String(destinationName ?? "").trim() || "—"}
            </span>
            <span className="user-tooltip-address-pro">Destino</span>
          </li>
        </ul>
      </div>,
      document.body
    );

  return (
    <>
      <div
        ref={anchorRef}
        className="location-origin-stops-pro"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        tabIndex={0}
        aria-label={`Origen: ${origin || "no indicado"}, ${countLabel} adicionales`}
        aria-describedby={open ? tooltipId : undefined}
      >
        <div className="location-pill-pro location-origin">{origin || "—"}</div>
        <span className="user-extra-pro location-stops-badge-pro">{countLabel}</span>
      </div>
      {tooltip}
    </>
  );
}

/** Avatar + nombre truncado + tooltip con nombre completo (autorizador / conductor). */
function UserPersonTooltipCell({
  name,
  title,
  avatarClassName,
  hideAvatar = false,
}: {
  name?: string | null;
  title: string;
  avatarClassName?: string;
  hideAvatar?: boolean;
}) {
  const displayName = String(name ?? "").trim();
  const tooltipId = React.useId();
  const { anchorRef, open, style, showTooltip, hideTooltip } = useUserTooltipPosition();

  if (!displayName) return <>{"—"}</>;

  const tooltip =
    open &&
    createPortal(
      <div
        className="user-tooltip-pro user-tooltip-pro--portal"
        id={tooltipId}
        role="tooltip"
        style={style}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        <p className="user-tooltip-title-pro">{title}</p>
        <ul className="user-tooltip-list-pro">
          <li className="user-tooltip-item-pro">
            <span className="user-tooltip-passenger-pro">{displayName}</span>
          </li>
        </ul>
      </div>,
      document.body
    );

  return (
    <>
      <div
        ref={anchorRef}
        className={`user-cell-pro user-cell-with-tooltip-pro${hideAvatar ? " user-cell-pro--name-only" : ""}`}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        tabIndex={0}
        aria-label={displayName}
        aria-describedby={open ? tooltipId : undefined}
      >
        {!hideAvatar && (
          <div className={`user-avatar-pro ${avatarClassName ?? ""}`.trim()} aria-hidden>
            {getInitial(displayName)}
          </div>
        )}
        <div className="user-text-pro">
          <span className="user-name-pro">{displayName}</span>
        </div>
      </div>
      {tooltip}
    </>
  );
}

/** Recorta la descripción del cambio para un resumen breve en el popover. */
function summarizeChange(raw: unknown, maxLength = 90): string {
  const s = String(raw ?? "").trim();
  if (!s) return "Sin detalle disponible";
  return s.length > maxLength ? `${s.slice(0, maxLength - 1).trimEnd()}…` : s;
}

/** Badge "Modificado": al hacer click muestra un resumen breve del último cambio. */
function ModifiedChangeBadge({
  summary,
  at,
}: {
  summary?: string | null;
  at?: string | null;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>({});

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 10;
    const popoverWidth = Math.min(260, window.innerWidth - margin * 2);
    let left = Math.min(r.left, window.innerWidth - popoverWidth - margin);
    left = Math.max(margin, left);

    const spaceBelow = window.innerHeight - r.bottom - margin;
    const preferBelow = spaceBelow >= 100;

    setStyle({
      position: "fixed",
      left,
      width: popoverWidth,
      zIndex: 15000,
      ...(preferBelow
        ? { top: r.bottom + 8, bottom: "auto" }
        : { top: "auto", bottom: window.innerHeight - r.top + 8 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const onReflow = () => updatePosition();
    const onOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    document.addEventListener("mousedown", onOutside, true);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
      document.removeEventListener("mousedown", onOutside, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, updatePosition]);

  const popoverId = React.useId();
  const shortSummary = summarizeChange(summary);
  const whenLabel = at ? formatCargaDiariaInstant(new Date(at)) : "";

  const popover =
    open &&
    createPortal(
      <div
        ref={popoverRef}
        id={popoverId}
        className="svc-modified-popover svc-modified-popover--portal"
        role="dialog"
        aria-label="Resumen del último cambio"
        style={style}
      >
        <p className="svc-modified-popover__title">Último cambio</p>
        <p className="svc-modified-popover__text">{shortSummary}</p>
        {whenLabel && <p className="svc-modified-popover__meta">{whenLabel}</p>}
      </div>,
      document.body
    );

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="svc-modified-flag"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-describedby={open ? popoverId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        Modificado
      </button>
      {popover}
    </>
  );
}

function areServiceRowPropsEqual(prev: ServiceRowProps, next: ServiceRowProps): boolean {
  return (
    prev.s === next.s &&
    prev.isStatusLoading === next.isStatusLoading &&
    prev.isStatusMenuOpen === next.isStatusMenuOpen &&
    prev.isAssignDriverLoading === next.isAssignDriverLoading &&
    prev.isReleaseDriverLoading === next.isReleaseDriverLoading &&
    prev.isDriversLoading === next.isDriversLoading &&
    prev.driverOptions === next.driverOptions &&
    prev.dayToneIndex === next.dayToneIndex &&
    prev.nowMs === next.nowMs &&
    prev.onEdit === next.onEdit &&
    prev.onDelete === next.onDelete &&
    prev.onEditRoute === next.onEditRoute &&
    prev.onDuplicate === next.onDuplicate &&
    prev.onRetorno === next.onRetorno &&
    prev.onStatusTransition === next.onStatusTransition &&
    prev.onToggleStatusMenu === next.onToggleStatusMenu &&
    prev.onAssignDriver === next.onAssignDriver &&
    prev.onReleaseDriver === next.onReleaseDriver &&
    prev.onLoadDrivers === next.onLoadDrivers
  );
}

const ServiceRow = React.memo<ServiceRowProps>(
  function ServiceRow({
    s,
    onEdit,
    onDelete,
    onEditRoute,
    onDuplicate,
    onRetorno,
    onStatusTransition,
    isStatusLoading,
    isStatusMenuOpen,
    onToggleStatusMenu,
    onAssignDriver,
    isAssignDriverLoading,
    onReleaseDriver,
    isReleaseDriverLoading,
    driverOptions,
    isDriversLoading,
    onLoadDrivers,
    dayToneIndex = null,
    nowMs = Date.now(),
  }) {
    const passengerInfo = useMemo(() => {
      const details = Array.isArray(s.passenger_details)
        ? s.passenger_details
            .map((p: any) => ({
              name: getPassengerFullName(p?.name),
              address: String(p?.address ?? "").trim() || "Sin dirección",
            }))
            .filter((p: any) => p.name && p.name !== "—")
        : [];

      const fallback = splitPassengerLabel(s.passenger);
      const primaryName =
        details.length > 0 ? details[0].name : getPassengerFullName(fallback.name);
      const extraCount =
        details.length > 1
          ? details.length - 1
          : fallback.extra
            ? Number(fallback.extra.replace("+", ""))
            : 0;

      return { details, primaryName, extraCount };
    }, [s.passenger_details, s.passenger]);

    const companyStyle = useMemo(() => getCompanyColorStyle(s.company_name), [s.company_name]);
    const fullDate = formatDateDDMMYYYY(s.service_date);
    // Solo visual en tabla: DD-MM (año completo en title / aria-label)
    const shortDate = fullDate.replace(/-\d{4}$/, "") || "—";

    const st = String(s.status || "");
    const isVip = s.priority === "VIP";
    const isVoided = isVoidedStatus(st);
    const missingDriverUrgency = isVoided
      ? null
      : getMissingDriverUrgency(s, nowMs);
    const hasAssignedDriver =
      !isVoided &&
      !missingDriverUrgency &&
      (Boolean(s.driver) || Boolean(String(s.driver_name ?? "").trim()));
    const { label: statusLabel, icon: statusIcon } = getStatusDisplay(st);
    const nextStatus = getNextStatus(st);
    const canAdvance = Boolean(onStatusTransition && nextStatus && s.id);
    const nextLabel = nextStatus ? getStatusDisplay(nextStatus).label : "";

    const statusTriggerRef = useRef<HTMLButtonElement>(null);
    const [statusMenuStyle, setStatusMenuStyle] = useState<React.CSSProperties>({});

    const canAssignDriver = Boolean(
      onAssignDriver &&
        s.id &&
        String(s.status || "") === ServiceStatus.AUTORIZADO &&
        !String(s.driver_name || "").trim()
    );

    const showReleaseDriver = Boolean(
      onReleaseDriver &&
        s.id &&
        String(s.driver_name || "").trim() &&
        (st === ServiceStatus.ASIGNADO || st === ServiceStatus.EN_CURSO)
    );
    const canReleaseDriver = showReleaseDriver && st === ServiceStatus.ASIGNADO;
    const releaseDriverTitle = canReleaseDriver
      ? "Desasignar conductor"
      : st === ServiceStatus.EN_CURSO
        ? "No se puede desasignar el conductor mientras el servicio está en curso"
        : "Desasignar conductor";

    const assignDriverSelectOptions = useMemo(
      () =>
        driverOptions.map((opt) => ({
          value: String(opt.value),
          label: shortDriverOptionLabel(opt.label),
        })),
      [driverOptions]
    );

    const updateStatusMenuPosition = useCallback(() => {
      const el = statusTriggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const margin = 10;
      const menuWidth = 200;
      let left = Math.min(r.left, window.innerWidth - menuWidth - margin);
      left = Math.max(margin, left);

      const spaceBelow = window.innerHeight - r.bottom - margin;
      const preferBelow = spaceBelow >= 52;

      setStatusMenuStyle({
        position: "fixed",
        left,
        minWidth: 180,
        zIndex: 15000,
        visibility: "visible",
        ...(preferBelow
          ? { top: r.bottom + 8, bottom: "auto" }
          : { top: "auto", bottom: window.innerHeight - r.top + 8 }),
      });
    }, []);

    useLayoutEffect(() => {
      if (!isStatusMenuOpen) return;
      updateStatusMenuPosition();
      const onReflow = () => updateStatusMenuPosition();
      window.addEventListener("scroll", onReflow, true);
      window.addEventListener("resize", onReflow);
      return () => {
        window.removeEventListener("scroll", onReflow, true);
        window.removeEventListener("resize", onReflow);
      };
    }, [isStatusMenuOpen, updateStatusMenuPosition]);

    const statusNextMenu =
      isStatusMenuOpen &&
      nextStatus &&
      createPortal(
        <div
          className="status-next-menu status-next-menu--portal"
          role="menu"
          style={{ visibility: "hidden", ...statusMenuStyle }}
        >
          <button
            type="button"
            className="status-next-menu__item"
            role="menuitem"
            disabled={isStatusLoading}
            onClick={() => {
              onToggleStatusMenu(Number(s.id));
              onStatusTransition?.(s, nextStatus);
            }}
          >
            <span className="status-next-menu__label">
              {isStatusLoading ? "Actualizando..." : `Avanzar a ${nextLabel}`}
            </span>
            <span className="status-next-menu__icon" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14M13 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
        </div>,
        document.body
      );

    return (
      <tr
        className={[
          "table-row-pro",
          isVip && !isVoided ? "row-vip" : "",
          isVoided ? "row-voided" : "",
          !isVoided && missingDriverUrgency
            ? `row-missing-driver row-missing-driver--${missingDriverUrgency}`
            : "",
          !isVoided && !isVip && s.row_color
            ? `row-location-${s.row_color}`
            : "",
          hasAssignedDriver && !isVip ? "row-has-driver" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <td className="td-pro td-id" data-label="ID">
          <div className="id-cell-pro">
            <span className="id-badge-pro">#{s.id || "—"}</span>
            {isVip && !isVoided && (
              <span className="svc-vip-flag" title="Prioridad VIP">
                <span className="svc-attention-pulse svc-attention-pulse--vip" aria-hidden />
                VIP
              </span>
            )}
          </div>
        </td>
        <td className="td-pro td-company" data-label="Empresa">
          <span className="company-text-pro" style={companyStyle} title={s.company_name || "—"}>
            {s.company_name || "—"}
          </span>
        </td>
        <td className="td-pro td-driver" data-label="Conductor">
          {canAssignDriver ? (
            <div
              className={[
                "driver-assign-select-wrap",
                missingDriverUrgency
                  ? `driver-assign-select-wrap--urgent driver-assign-select-wrap--${missingDriverUrgency}`
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              title={
                missingDriverUrgency
                  ? "Sin conductor y próximo a iniciar"
                  : undefined
              }
            >
              {missingDriverUrgency && (
                <span
                  className={`svc-attention-pulse svc-attention-pulse--driver-${missingDriverUrgency}`}
                  aria-hidden
                />
              )}
              <BaseSelect
                label="Conductor"
                name={`assign-driver-${s.id}`}
                value=""
                hideLabel
                size="compact"
                searchable
                preserveCase
                menuMinWidth={280}
                includePlaceholderOption={false}
                disabled={isAssignDriverLoading}
                placeholder={
                  isAssignDriverLoading
                    ? "Asignando…"
                    : isDriversLoading
                      ? "Cargando…"
                      : "Asignar conductor"
                }
                searchPlaceholder="Buscar…"
                options={assignDriverSelectOptions}
                onOpenChange={(open) => {
                  if (open) onLoadDrivers?.();
                }}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  if (!Number.isFinite(next) || next <= 0) return;
                  onAssignDriver?.(s, next);
                }}
              />
            </div>
          ) : missingDriverUrgency ? (
            <span
              className={`svc-driver-empty svc-driver-empty--${missingDriverUrgency}`}
              title="Sin conductor y próximo a iniciar"
            >
              <span
                className={`svc-attention-pulse svc-attention-pulse--driver-${missingDriverUrgency}`}
                aria-hidden
              />
              Sin conductor
            </span>
          ) : (
            <UserPersonTooltipCell
              name={s.driver_name}
              title="Conductor"
              hideAvatar
            />
          )}
        </td>
        <td className="td-pro td-date" data-label="Fecha">
          <div
            className={[
              "dt-card-pro",
              "dt-card-date-pro",
              !isVoided && dayToneIndex != null ? `day-tone-${dayToneIndex % 4}` : "",
            ]
              .filter(Boolean)
              .join(" ")}
            title={fullDate || undefined}
          >
            <span className="dt-icon-pro" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            <span className="dt-value-pro dt-value-date-pro" aria-label={fullDate || undefined}>
              {shortDate}
            </span>
          </div>
        </td>
        <td className="td-pro td-time" data-label="Hora">
          <div className="dt-card-pro dt-card-time-pro">
            <span className="dt-icon-pro" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            <span className="dt-value-pro dt-value-time-pro">
              {formatTimeHHMM(s.service_time) || "—"}
            </span>
          </div>
        </td>
        <td className="td-pro td-user" data-label="Pasajeros">
          <UserPassengerTooltipCell
            primaryName={passengerInfo.primaryName}
            extraCount={passengerInfo.extraCount}
            details={passengerInfo.details}
          />
        </td>
        <td className="td-pro td-location td-origin" data-label="Origen">
          <OriginStopsCell
            originName={s.origin_name}
            destinationName={s.destination_name}
            stops={Array.isArray(s.stops) ? s.stops : []}
          />
        </td>
        <td className="td-pro td-location td-destination" data-label="Destino">
          <div
            className="location-pill-pro location-destination"
            title={s.destination_name || undefined}
            aria-label={
              s.destination_name ? `Destino: ${s.destination_name}` : "Destino no indicado"
            }
          >
            {s.destination_name || "—"}
          </div>
        </td>
        <td className="td-pro td-assignee" data-label="Autorizador">
          <UserPersonTooltipCell name={s.authorizer_name} title="Autorizador" hideAvatar />
        </td>
        <td className="td-pro td-status" data-label="Estado">
          <div className="status-cell-pro">
            {!canAdvance ? (
              <span
                className={`status-badge-table ${getStatusColor(st)}`}
                role="status"
                aria-label={`Estado del servicio: ${statusLabel}`}
              >
                <span className="status-badge-table__icon">{statusIcon}</span>
                <span className="status-badge-table__text">{statusLabel}</span>
              </span>
            ) : (
              <div className="status-action-wrap">
                <button
                  ref={statusTriggerRef}
                  type="button"
                  className={`status-badge-table status-badge-trigger ${getStatusColor(st)}`}
                  aria-haspopup="menu"
                  aria-expanded={isStatusMenuOpen}
                  aria-label={`Estado actual: ${statusLabel}. Mostrar siguiente estado`}
                  disabled={isStatusLoading}
                  onClick={() => {
                    if (s.id) onToggleStatusMenu(Number(s.id));
                  }}
                >
                  <span className="status-badge-table__icon">{statusIcon}</span>
                  <span className="status-badge-table__text">{statusLabel}</span>
                  <span className="status-badge-table__chevron" aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6 9l6 6 6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>

                {statusNextMenu}
              </div>
            )}
            {s.is_modified && (
              <ModifiedChangeBadge
                summary={s.last_modification_summary}
                at={s.last_modification_at}
              />
            )}
          </div>
        </td>
        <td className="td-pro td-actions">
          <div className="actions-group-pro">
            <span className="action-tooltip-wrap action-wrap-edit" title="Editar servicio">
              <BaseTableAction
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                onClick={() => onEdit(s)}
                title="Editar servicio"
              />
            </span>
            {onRetorno && (
              <span className="action-tooltip-wrap action-wrap-return" title="Generar retorno">
                <BaseTableAction
                  icon={
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 14L4 9l5-5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 20v-7a4 4 0 00-4-4H4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  onClick={() => onRetorno(s)}
                  title="Generar retorno"
                />
              </span>
            )}
            {showReleaseDriver && (
              <span
                className="action-tooltip-wrap action-wrap-release"
                title={releaseDriverTitle}
              >
                <BaseTableAction
                  disabled={!canReleaseDriver || isReleaseDriverLoading}
                  icon={
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18 8l4 4m0-4l-4 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  onClick={() => {
                    if (canReleaseDriver && !isReleaseDriverLoading) onReleaseDriver?.(s);
                  }}
                  title={releaseDriverTitle}
                />
              </span>
            )}
            {onDuplicate && (
              <span className="action-tooltip-wrap action-wrap-duplicate" title="Duplicar servicio">
                <BaseTableAction
                  icon={
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                      <path
                        d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  onClick={() => onDuplicate(s)}
                  title="Duplicar servicio"
                />
              </span>
            )}
            {onEditRoute && (
              <span className="action-tooltip-wrap action-wrap-route" title="Modificar ruta">
                <BaseTableAction
                  icon={
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  }
                  onClick={() => onEditRoute(s)}
                  title="Modificar ruta"
                />
              </span>
            )}
            {/* Eliminar: oculto temporalmente (prop onDelete se mantiene) */}
            {false && (
              <span className="action-tooltip-wrap action-wrap-delete" title="Eliminar servicio">
                <BaseTableAction
                  icon={
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <line x1="10" y1="11" x2="10" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <line x1="14" y1="11" x2="14" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  }
                  onClick={() => s.id && onDelete(s.id)}
                  title="Eliminar servicio"
                />
              </span>
            )}
          </div>
        </td>
      </tr>
    );
  },
  areServiceRowPropsEqual
);

ServiceRow.displayName = "ServiceRow";

const ServicesTable: React.FC<Props> = ({
  data,
  totalCount,
  companyOptions = [],
  passengerOptions = [],
  loading,
  onEdit,
  onDelete,
  onEditRoute,
  onDuplicate,
  onRetorno,
  onStatusTransition,
  statusTransitionLoadingIds = [],
  onAssignDriver,
  assignDriverLoadingIds = [],
  onReleaseDriver,
  releaseDriverLoadingIds = [],
  driverOptionsByCompany = {},
  onLoadDriversForCompany,
  driversLoadingCompanyIds = [],
  onFiltersChange,
}) => {
  // Filtros persistidos en sessionStorage: se restauran al volver a esta página
  const [filters, setFilters] = useSessionStorageState<ServicesTableFilters>(
    SERVICES_TABLE_FILTERS_STORAGE_KEY,
    createDefaultServicesTableFilters()
  );
  const generalDefaultsSynced = useRef(false);

  useEffect(() => {
    if (generalDefaultsSynced.current) return;
    generalDefaultsSynced.current = true;
    const patched = applyGeneralModeDateDefaults(filters);
    if (patched.fechaDesde !== filters.fechaDesde) {
      setFilters(patched);
      onFiltersChange?.(patched);
    }
    // Solo al montar: hidratar fecha por defecto si modo general estaba persistido
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Orden elegido por el usuario (null = orden por defecto: fecha + hora ascendente)
  const [sort, setSort] = useSessionStorageState<ServicesSortState>(
    SERVICES_TABLE_SORT_STORAGE_KEY,
    null
  );

  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [openStatusMenuId, setOpenStatusMenuId] = useState<number | null>(null);
  const filtersPanelInnerRef = useRef<HTMLDivElement>(null);
  const [filtersPanelHeight, setFiltersPanelHeight] = useState(0);

  const measureFiltersPanel = useCallback(() => {
    if (!filtersPanelInnerRef.current) return;
    setFiltersPanelHeight(filtersPanelInnerRef.current.scrollHeight);
  }, []);

  // 🔹 Obtener valores únicos para los filtros
  const uniqueCompanies = useMemo(() => {
    const byId = new Map<number, string>();
    if (companyOptions.length > 0) {
      for (const option of companyOptions) {
        const id = Number(option.value);
        if (!Number.isNaN(id)) byId.set(id, option.label);
      }
    } else {
      for (const s of data) {
        if (s.company != null && s.company_name) {
          byId.set(Number(s.company), String(s.company_name));
        }
      }
    }
    // El filtro de empresa solo debe mostrar estas 4 empresas, en este orden,
    // sin importar qué otras empresas existan en los datos o en companyOptions.
    const allowedOrder = ["dgm", "el abra", "enaex", "dmh"];
    return Array.from(byId.entries())
      .map(([id, name]) => ({ id, name }))
      .filter(({ name }) => allowedOrder.includes(name.trim().toLowerCase()))
      .sort(
        (a, b) =>
          allowedOrder.indexOf(a.name.trim().toLowerCase()) -
          allowedOrder.indexOf(b.name.trim().toLowerCase())
      );
  }, [data, companyOptions]);

  /** Conteo de servicios por estado (para los chips de estado) */
  const statusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of data) {
      const st = String(s.status ?? "").trim();
      if (!st) continue;
      counts.set(st, (counts.get(st) ?? 0) + 1);
    }
    return counts;
  }, [data]);

  /** Chips de estado: los principales siempre visibles + los presentes en datos */
  const statusChips = useMemo(() => {
    const keys = new Set<string>(DEFAULT_STATUS_CHIPS);
    statusCounts.forEach((_, st) => keys.add(st));
    if (filters.estado) keys.add(filters.estado);
    const ordered = CANONICAL_STATUS_ORDER.filter((k) => keys.has(k));
    const extras = Array.from(keys).filter((k) => !CANONICAL_STATUS_ORDER.includes(k)).sort();
    return [...ordered, ...extras];
  }, [statusCounts, filters.estado]);

  const uniqueOrigins = useMemo(() => {
    const byId = new Map<number, string>();
    for (const s of data) {
      if (s.origin != null && s.origin_name) {
        const id = Number(s.origin);
        if (!Number.isNaN(id)) byId.set(id, String(s.origin_name));
      }
    }
    return Array.from(byId.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [data]);

  const uniqueDestinations = useMemo(() => {
    const byId = new Map<number, string>();
    for (const s of data) {
      if (s.destination != null && s.destination_name) {
        const id = Number(s.destination);
        if (!Number.isNaN(id)) byId.set(id, String(s.destination_name));
      }
    }
    return Array.from(byId.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [data]);

  const uniqueCostCenters = useMemo(() => {
    const byId = new Map<number, string>();
    for (const s of data) {
      if (s.cost_center != null && s.cost_center_name) {
        const id = Number(s.cost_center);
        if (!Number.isNaN(id)) byId.set(id, String(s.cost_center_name));
      }
    }
    return Array.from(byId.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [data]);

  // 🔹 Modo de vista del listado (excluyentes entre sí)
  const viewMode: "daily" | "general" | "recent" = filters.recentMode
    ? "recent"
    : filters.generalMode
      ? "general"
      : "daily";

  useLayoutEffect(() => {
    if (!isFiltersVisible) {
      setFiltersPanelHeight(0);
      return;
    }
    measureFiltersPanel();
    const node = filtersPanelInnerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measureFiltersPanel());
    observer.observe(node);
    return () => observer.disconnect();
  }, [isFiltersVisible, measureFiltersPanel, filters, data.length, viewMode]);

  const selectedCompanyName = useMemo(() => {
    if (!filters.empresa) return "";
    return uniqueCompanies.find(({ id }) => String(id) === filters.empresa)?.name ?? filters.empresa;
  }, [filters.empresa, uniqueCompanies]);

  const selectedOriginName = useMemo(() => {
    if (!filters.origen) return "";
    return uniqueOrigins.find(({ id }) => String(id) === filters.origen)?.name ?? filters.origen;
  }, [filters.origen, uniqueOrigins]);

  const selectedDestinationName = useMemo(() => {
    if (!filters.destino) return "";
    return uniqueDestinations.find(({ id }) => String(id) === filters.destino)?.name ?? filters.destino;
  }, [filters.destino, uniqueDestinations]);

  const selectedCostCenterName = useMemo(() => {
    if (!filters.centroCosto) return "";
    return (
      uniqueCostCenters.find(({ id }) => String(id) === filters.centroCosto)?.name ??
      filters.centroCosto
    );
  }, [filters.centroCosto, uniqueCostCenters]);

  const selectedPassengerLabels = useMemo(() => {
    if (filters.pasajeros.length === 0) return [];
    const byValue = new Map(
      passengerOptions.map((option) => [Number(option.value), option.label] as const)
    );
    return filters.pasajeros.map((id) => byValue.get(Number(id)) ?? `ID ${id}`);
  }, [filters.pasajeros, passengerOptions]);

  const activeFilterChips = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];

    if (viewMode !== "daily") {
      chips.push({
        key: "viewMode",
        label: "Vista",
        value: viewMode === "general" ? "Modo general" : "Últimos agregados",
      });
    }
    if (filters.id) chips.push({ key: "id", label: "ID", value: filters.id });
    if (selectedCompanyName) chips.push({ key: "empresa", label: "Empresa", value: selectedCompanyName });
    if (filters.enaexCode) {
      chips.push({ key: "enaexCode", label: "Código Enaex", value: filters.enaexCode });
    }
    if (filters.fechaDesde || filters.horaDesde || filters.fechaHasta || filters.horaHasta) {
      const from = [filters.fechaDesde, filters.horaDesde].filter(Boolean).join(" ");
      const to = [filters.fechaHasta, filters.horaHasta].filter(Boolean).join(" ");
      chips.push({
        key: "fechaDesde",
        label: "Rango",
        value: [from && `Desde ${from}`, to && `Hasta ${to}`].filter(Boolean).join(" · "),
      });
    }
    if (selectedOriginName) chips.push({ key: "origen", label: "Origen", value: selectedOriginName });
    if (selectedDestinationName) {
      chips.push({ key: "destino", label: "Destino", value: selectedDestinationName });
    }
    if (selectedCostCenterName) {
      chips.push({ key: "centroCosto", label: "Centro de costo", value: selectedCostCenterName });
    }
    if (filters.estado) {
      chips.push({ key: "estado", label: "Estado", value: getStatusDisplay(filters.estado).label });
    }
    if (filters.proximasHoras) {
      const proximasLabel =
        PROXIMAS_HORAS_OPTIONS.find((option) => option.value === filters.proximasHoras)?.label ??
        filters.proximasHoras;
      chips.push({ key: "proximasHoras", label: "Salida", value: proximasLabel });
    }
    if (selectedPassengerLabels.length > 0) {
      const preview = selectedPassengerLabels.slice(0, 2).join(", ");
      const remainder = selectedPassengerLabels.length - 2;
      chips.push({
        key: "pasajeros",
        label: "Pasajeros",
        value: remainder > 0 ? `${preview} +${remainder}` : preview,
      });
    }

    return chips;
  }, [
    filters.id,
    filters.enaexCode,
    filters.fechaDesde,
    filters.horaDesde,
    filters.fechaHasta,
    filters.horaHasta,
    filters.estado,
    filters.proximasHoras,
    selectedCompanyName,
    selectedOriginName,
    selectedDestinationName,
    selectedCostCenterName,
    selectedPassengerLabels,
    viewMode,
  ]);

  const handleRemoveFilterChip = useCallback(
    (key: ActiveFilterChipKey) => {
      if (key === "viewMode") {
        const newFilters = {
          ...filters,
          generalMode: false,
          recentMode: false,
        };
        setFilters(newFilters);
        onFiltersChange?.(newFilters);
        return;
      }

      const nextFilters = { ...filters };
      if (key === "pasajeros") {
        nextFilters.pasajeros = [];
      } else if (key === "fechaDesde") {
        nextFilters.fechaDesde = "";
        nextFilters.horaDesde = "";
        nextFilters.fechaHasta = "";
        nextFilters.horaHasta = "";
      } else {
        nextFilters[key] = "";
      }

      setFilters(nextFilters);
      onFiltersChange?.(nextFilters);
    },
    [filters, onFiltersChange, setFilters]
  );

  /**
   * Orden mostrado en cabeceras: si el usuario no eligió columna, la vista
   * diaria/general ya viene ordenada por fecha + hora ascendente (criterio principal).
   */
  const effectiveSort: ServicesSortState =
    sort ?? (viewMode === "recent" ? null : { key: "fecha", dir: "asc" });

  // Orden: usuario > default por vista > sin orden (recientes = backend)
  const sortedData = useMemo(() => {
    const activeSort =
      sort ?? (viewMode === "recent" ? null : { key: "fecha" as ServicesSortKey, dir: "asc" as const });
    if (!activeSort) return data;
    return sortServices(data, activeSort);
  }, [data, sort, viewMode]);

  const statusLoadingIds = useMemo(
    () => new Set(statusTransitionLoadingIds),
    [statusTransitionLoadingIds]
  );

  const assignDriverLoadingSet = useMemo(
    () => new Set(assignDriverLoadingIds),
    [assignDriverLoadingIds]
  );

  const releaseDriverLoadingSet = useMemo(
    () => new Set(releaseDriverLoadingIds),
    [releaseDriverLoadingIds]
  );

  const driversLoadingCompanySet = useMemo(
    () => new Set(driversLoadingCompanyIds),
    [driversLoadingCompanyIds]
  );

  const toggleSort = useCallback(
    (key: ServicesSortKey) => {
      setSort((prev) => {
        const current =
          prev ?? (viewMode === "recent" ? null : { key: "fecha" as ServicesSortKey, dir: "asc" as const });
        if (!current || current.key !== key) return { key, dir: "asc" };
        if (current.dir === "asc") return { key, dir: "desc" };
        return null;
      });
    },
    [setSort, viewMode]
  );

  const handleToggleStatusMenu = useCallback((id: number) => {
    setOpenStatusMenuId((prev) => (prev === id ? null : id));
  }, []);

  const handleLoadDriversForService = useCallback(
    (service: Service) => {
      const companyId = service.company != null ? Number(service.company) : NaN;
      if (
        Number.isFinite(companyId) &&
        companyId > 0 &&
        !(companyId in driverOptionsByCompany) &&
        onLoadDriversForCompany
      ) {
        void onLoadDriversForCompany(companyId);
      }
    },
    [driverOptionsByCompany, onLoadDriversForCompany]
  );

  /** Reloj para urgencia visual (VIP / sin conductor / rango carga diaria) */
  const [cargaDiariaNow, setCargaDiariaNow] = useState(() => Date.now());

  useEffect(() => {
    setCargaDiariaNow(Date.now());
    const id = window.setInterval(() => setCargaDiariaNow(Date.now()), CARGA_DIARIA_REFRESH_MS);
    return () => window.clearInterval(id);
  }, []);

  const cargaDiariaRango = useMemo(
    () => getCargaDiariaRangeFromNow(new Date(cargaDiariaNow)),
    [cargaDiariaNow]
  );

  // Salidas próximas: filtrado en servidor (proximas_horas); aquí solo orden local.
  const filteredData = sortedData;
  const resultsCount = totalCount ?? filteredData.length;

  /** Si hay más de un día visible, asigna tonos de la misma paleta cálida. */
  const dayToneByDate = useMemo(() => {
    const keys = new Set<string>();
    for (const s of filteredData) {
      const key = toServiceDateKey(s.service_date);
      if (key) keys.add(key);
    }
    if (keys.size <= 1) return null;
    const sorted = Array.from(keys).sort();
    const map = new Map<string, number>();
    sorted.forEach((key, index) => map.set(key, index % 4));
    return map;
  }, [filteredData]);

  // 🔹 Helper para actualizar filtros y notificar al padre
  const updateFilter = (key: string, value: string | boolean) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const setViewMode = (mode: "daily" | "general" | "recent") => {
    if (mode === viewMode) return;
    let newFilters: ServicesTableFilters = {
      ...filters,
      generalMode: mode === "general",
      recentMode: mode === "recent",
    };

    if (mode === "general") {
      newFilters = applyGeneralModeDateDefaults({
        ...newFilters,
        generalMode: true,
        recentMode: false,
      });
    } else if (mode === "daily") {
      newFilters = {
        ...newFilters,
        generalMode: false,
        recentMode: false,
        fechaDesde: "",
        fechaHasta: "",
        horaDesde: "",
        horaHasta: "",
      };
    } else {
      newFilters = {
        ...newFilters,
        generalMode: false,
        recentMode: true,
        fechaDesde: "",
        fechaHasta: "",
        horaDesde: "",
        horaHasta: "",
      };
    }

    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  // 🔹 Limpiar filtros (también limpia lo persistido en sessionStorage)
  const clearFilters = () => {
    const emptyFilters = createDefaultServicesTableFilters();
    setFilters(emptyFilters);
    setSort(null);
    onFiltersChange?.(emptyFilters);
  };

  const viewHintText =
    viewMode === "recent"
      ? "Últimos agregados: ordenados por fecha de creación."
      : viewMode === "general"
        ? `Modo general: desde el ${
            filters.fechaDesde
              ? formatDateDDMMYYYY(filters.fechaDesde)
              : formatDateDDMMYYYY(getGeneralModeDefaultStartDateYmd())
          } en adelante.`
        : `Carga diaria: ${cargaDiariaRango.desde} — ${cargaDiariaRango.hasta}`;

  const hasActiveFilters = Boolean(
    filters.id ||
      filters.fechaDesde ||
      filters.horaDesde ||
      filters.origen ||
      filters.destino ||
      filters.centroCosto ||
      filters.empresa ||
      filters.enaexCode ||
      filters.estado ||
      filters.proximasHoras ||
      filters.pasajeros.length > 0 ||
      filters.generalMode ||
      filters.recentMode
  );

  return (
    <div className="services-table-container-pro">
      {/* 🎨 BARRA DE FILTROS (compacta) */}
      <div className="filters-bar-pro filters-bar-pro--compact">
        <div className="filters-sticky-pro">
          <div className="filters-sticky-mode-pro" role="group" aria-label="Modo de vista del listado">
            <span className="filters-sticky-mode-label">Vista</span>
            <div className="filter-mode-btns-pro filter-mode-btns-compact-pro">
              <button
                type="button"
                className={`filter-mode-btn-pro ${viewMode === "daily" ? "is-active is-active--daily" : ""}`}
                onClick={() => setViewMode("daily")}
                aria-pressed={viewMode === "daily"}
                title={viewMode === "daily" ? viewHintText : "Carga diaria"}
              >
                Carga diaria
              </button>
              <button
                type="button"
                className={`filter-mode-btn-pro ${viewMode === "general" ? "is-active is-active--general" : ""}`}
                onClick={() => setViewMode("general")}
                aria-pressed={viewMode === "general"}
                title={viewMode === "general" ? viewHintText : "Modo general"}
              >
                Modo general
              </button>
              <button
                type="button"
                className={`filter-mode-btn-pro ${viewMode === "recent" ? "is-active is-active--recent" : ""}`}
                onClick={() => setViewMode("recent")}
                aria-pressed={viewMode === "recent"}
                title={viewMode === "recent" ? viewHintText : "Últimos agregados"}
              >
                Últimos agregados
              </button>
            </div>
          </div>

          <div className="filters-header-actions-pro">
            <span
              className="results-count-pro"
              title={`${resultsCount} resultado${resultsCount !== 1 ? "s" : ""}${
                totalCount != null && filteredData.length < totalCount
                  ? ` (mostrando ${filteredData.length} en esta página)`
                  : ""
              }. ${viewHintText}`}
            >
              {resultsCount.toLocaleString("es-CL")}
            </span>
            <button
              type="button"
              className="filters-toggle-btn-pro"
              onClick={() => setIsFiltersVisible((v) => !v)}
              aria-expanded={isFiltersVisible}
              aria-controls="services-filters-panel"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Filtros</span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
                className={isFiltersVisible ? "filters-toggle-btn-pro__chev is-open" : "filters-toggle-btn-pro__chev"}
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {hasActiveFilters && (
              <button className="clear-filters-btn-pro" onClick={clearFilters} title="Limpiar filtros">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Limpiar
              </button>
            )}
          </div>
        </div>

        <p className="filters-sticky-hint-pro visually-hidden" aria-live="polite">
          {viewHintText}
        </p>

        <div
          id="services-filters-panel"
          className={`filters-panel-body${isFiltersVisible ? " is-open" : ""}`}
          aria-hidden={!isFiltersVisible}
          style={{ maxHeight: isFiltersVisible ? filtersPanelHeight : 0 }}
        >
        <div ref={filtersPanelInnerRef} className="filters-panel-inner">
        <div className="filters-summary-pro" aria-live="polite">
          <div className="filters-summary-copy-pro">
            <span className="filters-summary-eyebrow-pro">Contexto actual</span>
            <p className="filters-summary-text-pro">
              {activeFilterChips.length > 0
                ? `${activeFilterChips.length} filtro${activeFilterChips.length !== 1 ? "s" : ""} aplicado${activeFilterChips.length !== 1 ? "s" : ""}.`
                : "Sin filtros adicionales: estás viendo el listado base según la vista seleccionada."}
            </p>
          </div>

          {activeFilterChips.length > 0 && (
            <div className="filters-active-chips-pro" role="list" aria-label="Filtros activos">
              {activeFilterChips.map((chip) => (
                <span key={`${chip.key}-${chip.value}`} className="filters-active-chip-pro" role="listitem">
                  <span className="filters-active-chip-pro__text">
                    <strong>{chip.label}:</strong> {chip.value}
                  </span>
                  <button
                    type="button"
                    className="filters-active-chip-pro__remove"
                    onClick={() => handleRemoveFilterChip(chip.key)}
                    aria-label={`Quitar filtro ${chip.label}`}
                    title={`Quitar filtro ${chip.label}`}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M18 6L6 18M6 6l12 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

      <div className="filters-quick-pro">
        {/* Bloque 1: ID + Empresa + Fecha/hora (una sola fila compacta) */}
        <div className="filters-quick-section-pro filters-quick-section-primary-pro">
          <div className="filter-group-pro filter-group-id-pro">
            <label className="filter-label-pro" htmlFor="svc-filter-id">ID</label>
            <input
              id="svc-filter-id"
              type="text"
              className="filter-input-pro filter-input-id-pro"
              placeholder="#"
              value={filters.id}
              onChange={(e) => updateFilter('id', e.target.value)}
            />
          </div>

          <div className="filter-group-pro filter-group-chips-pro filter-group-empresa-pro">
            <label className="filter-label-pro">Empresa</label>
            <div className="filter-chips-pro" role="group" aria-label="Filtrar por empresa">
              {uniqueCompanies.length === 0 ? (
                <span className="filter-empty-hint-pro">Sin empresas</span>
              ) : (
                uniqueCompanies.map(({ id, name }) => {
                  const value = String(id);
                  const isActive = filters.empresa === value;
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`filter-chip-pro${isActive ? " is-active" : ""}`}
                      aria-pressed={isActive}
                      onClick={() => {
                        const nextCompany = isActive ? "" : value;
                        const newFilters = {
                          ...filters,
                          empresa: nextCompany,
                          enaexCode: nextCompany && isEnaexCompany(name) ? filters.enaexCode : "",
                        };
                        setFilters(newFilters);
                        onFiltersChange?.(newFilters);
                      }}
                    >
                      {name}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {isEnaexCompany(selectedCompanyName) && (
            <div className="filter-group-pro filter-group-enaex-pro">
              <label className="filter-label-pro" htmlFor="svc-filter-enaex-code">
                Código ENAEX
              </label>
              <div className="filter-enaex-input-wrap">
                <input
                  id="svc-filter-enaex-code"
                  type="search"
                  className="filter-input-pro filter-input-enaex-pro"
                  placeholder="Buscar código"
                  autoComplete="off"
                  value={filters.enaexCode}
                  onChange={(e) => updateFilter("enaexCode", e.target.value)}
                />
                {filters.enaexCode && (
                  <button
                    type="button"
                    className="filter-enaex-clear-pro"
                    onClick={() => updateFilter("enaexCode", "")}
                    aria-label="Limpiar código ENAEX"
                    title="Limpiar código"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="filter-group-pro filter-group-datetime-pro">
            <ServiceDateTimeRangeFilter
              value={{
                fromDate: filters.fechaDesde,
                fromTime: filters.horaDesde,
                toDate: filters.fechaHasta,
                toTime: filters.horaHasta,
              }}
              onChange={(v) => {
                const newFilters = {
                  ...filters,
                  fechaDesde: v.fromDate,
                  horaDesde: v.fromTime,
                  fechaHasta: v.toDate,
                  horaHasta: v.toTime,
                };
                setFilters(newFilters);
                onFiltersChange?.(newFilters);
              }}
            />
          </div>
        </div>

        {/* Origen + Destino + Pasajeros */}
        <div className="filters-quick-section-pro filters-quick-section-location-pro">
          <div className="filter-group-pro filter-group-select-pro filter-group-origin-pro">
            <BaseSelect
              label="Origen"
              name="origen"
              value={filters.origen}
              onChange={(e) => updateFilter("origen", e.target.value)}
              placeholder="Todos los orígenes"
              searchable
              preserveCase
              options={uniqueOrigins.map(({ id, name }) => ({
                value: String(id),
                label: name,
              }))}
            />
          </div>

          <div className="filter-group-pro filter-group-select-pro filter-group-destination-pro">
            <BaseSelect
              label="Destino"
              name="destino"
              value={filters.destino}
              onChange={(e) => updateFilter("destino", e.target.value)}
              placeholder="Todos los destinos"
              searchable
              preserveCase
              options={uniqueDestinations.map(({ id, name }) => ({
                value: String(id),
                label: name,
              }))}
            />
          </div>

          <div className="filter-group-pro filter-group-select-pro filter-group-cost-center-pro">
            <BaseSelect
              label="Centro de costo"
              name="centro_costo"
              value={filters.centroCosto}
              onChange={(e) => updateFilter("centroCosto", e.target.value)}
              placeholder="Todos los centros de costo"
              searchable
              preserveCase
              options={uniqueCostCenters.map(({ id, name }) => ({
                value: String(id),
                label: name,
              }))}
            />
          </div>

          <div className="filter-group-pro filter-group-users-pro">
            <MultiSelectSearchable
              className="svc-filter-passenger-multi svc-filter-user-search"
              label="Pasajeros"
              name="filter_passengers"
              placeholder="Buscar y seleccionar pasajero…"
              searchPlaceholder="Escribe para buscar por nombre…"
              preserveCase
              sortAlphabetically
              selectedFirst
              menuPortal
              value={filters.pasajeros}
              options={passengerOptions}
              onChange={(selected) => {
                const newFilters = { ...filters, pasajeros: selected };
                setFilters(newFilters);
                onFiltersChange?.(newFilters);
              }}
            />
          </div>
        </div>

        {/* Estado + Salidas próximas */}
        <div className="filters-quick-section-pro filters-quick-section-status-pro">
          <div className="filter-group-pro filter-group-chips-pro">
            <label className="filter-label-pro">Estado</label>
            <div className="filter-chips-pro" role="group" aria-label="Filtrar por estado">
              {statusChips.map((st) => {
                const isActive = filters.estado === st;
                const count = statusCounts.get(st) ?? 0;
                return (
                  <button
                    key={st}
                    type="button"
                    className={`filter-chip-pro filter-chip-status-pro ${STATUS_COLOR_CLASSES[st] ?? ""}${isActive ? " is-active" : ""}`}
                    aria-pressed={isActive}
                    onClick={() => updateFilter("estado", isActive ? "" : st)}
                  >
                    {getStatusDisplay(st).label}
                    <span className="filter-chip-count-pro">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="filter-group-pro filter-group-chips-pro filter-group-proximas-pro">
            <label className="filter-label-pro">Salidas próximas</label>
            <div className="filter-chips-pro" role="group" aria-label="Filtrar por salidas próximas">
              {PROXIMAS_HORAS_OPTIONS.map(({ value, label }) => {
                const isActive = filters.proximasHoras === value;
                return (
                  <button
                    key={value}
                    type="button"
                    className={`filter-chip-pro filter-chip-time-pro${isActive ? " is-active" : ""}`}
                    aria-pressed={isActive}
                    onClick={() => updateFilter("proximasHoras", isActive ? "" : value)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
        </div>
        </div>
    </div>

      {/* 🎨 TABLA PROFESIONAL */}
      <p className="visually-hidden" role="status" aria-live="polite">
        {loading
          ? "Cargando servicios…"
          : `${resultsCount} servicio${resultsCount === 1 ? "" : "s"} encontrado${resultsCount === 1 ? "" : "s"}.`}
      </p>
      <div className="table-wrapper-pro">
        <table className="services-table-pro" aria-busy={loading}>
          <caption className="visually-hidden">
            Listado de servicios
            {resultsCount > 0
              ? `, ${resultsCount} resultado${resultsCount === 1 ? "" : "s"}`
              : ""}
            . Usa los encabezados de columna para ordenar.
          </caption>
          <colgroup>
            <col className="col-id-pro" />
            <col className="col-company-pro" />
            <col className="col-driver-pro" />
            <col className="col-date-pro" />
            <col className="col-time-pro" />
            <col className="col-users-pro" />
            <col className="col-origin-pro" />
            <col className="col-destination-pro" />
            <col className="col-authorizer-pro" />
            <col className="col-status-pro" />
            <col className="col-actions-pro" />
          </colgroup>
          <thead>
            <tr>
              {TABLE_COLUMNS.map(({ sortKey, label, icon }) => {
                const isSorted = Boolean(
                  sortKey && effectiveSort && effectiveSort.key === sortKey
                );
                const dir = isSorted ? effectiveSort!.dir : undefined;
                if (!sortKey) {
                  return (
                    <th key={label} scope="col" className="th-service th-static">
                      <div className="th-content-pro th-static-content">
                        {icon ? <span className="th-col-icon-pro" aria-hidden>{icon}</span> : null}
                        <span>{label}</span>
                      </div>
                    </th>
                  );
                }
                return (
                  <th
                    key={label}
                    scope="col"
                    className={`th-service th-sortable${isSorted ? " is-sorted" : ""}`}
                    aria-sort={isSorted ? (dir === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <button
                      type="button"
                      className="th-sort-btn-pro"
                      onClick={() => toggleSort(sortKey)}
                      title={`Ordenar por ${label}`}
                    >
                      {icon ? <span className="th-col-icon-pro" aria-hidden>{icon}</span> : null}
                      <span>{label}</span>
                      <span className="th-sort-indicator-pro" aria-hidden>
                        {isSorted ? (
                          dir === "asc" ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <path d="M12 5v14M19 12l-7 7-7-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="th-sort-neutral-pro">
                            <path d="M8 9l4-4 4 4M8 15l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                    </button>
                  </th>
                );
              })}
              <th scope="col" className="th-service th-actions">
                <div className="th-content-pro th-actions-content">
                  Acciones
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={11} className="loading-cell-pro">
                  <div className="loading-content-pro">
                    <div className="spinner-pro" aria-hidden="true"></div>
                    <span>Cargando servicios...</span>
                  </div>
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={11} className="empty-cell-pro">
                  <div className="empty-content-pro">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <p className="empty-title-pro">No se encontraron servicios</p>
                    <p className="empty-subtitle-pro">
                      {data.length === 0
                        ? "Aún no hay servicios registrados"
                        : "Intenta ajustar los filtros"}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredData.map((s) => (
                <ServiceRow
                  key={s.id}
                  s={s}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onEditRoute={onEditRoute}
                  onDuplicate={onDuplicate}
                  onRetorno={onRetorno}
                  onStatusTransition={onStatusTransition}
                  isStatusLoading={Boolean(s.id && statusLoadingIds.has(s.id))}
                  isStatusMenuOpen={Boolean(s.id && openStatusMenuId === Number(s.id))}
                  onToggleStatusMenu={handleToggleStatusMenu}
                  onAssignDriver={onAssignDriver}
                  isAssignDriverLoading={Boolean(s.id && assignDriverLoadingSet.has(Number(s.id)))}
                  onReleaseDriver={onReleaseDriver}
                  isReleaseDriverLoading={Boolean(s.id && releaseDriverLoadingSet.has(Number(s.id)))}
                  driverOptions={
                    s.company != null ? driverOptionsByCompany[Number(s.company)] ?? [] : []
                  }
                  isDriversLoading={Boolean(
                    s.company != null && driversLoadingCompanySet.has(Number(s.company))
                  )}
                  onLoadDrivers={() => handleLoadDriversForService(s)}
                  dayToneIndex={
                    dayToneByDate
                      ? dayToneByDate.get(toServiceDateKey(s.service_date)) ?? null
                      : null
                  }
                  nowMs={cargaDiariaNow}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default React.memo(ServicesTable);
